/**
 * useSSE.ts — хук подписки на SSE прогресс транскодирования.
 *
 * Открывает EventSource на /api/videos/:id/status,
 * парсит события и закрывает соединение при финальном статусе.
 */

import { useEffect, useState } from 'react';
import type { TranscodeProgress } from '../types';
import { getAuthToken } from '../api/client';

interface UseSSEResult {
  status: TranscodeProgress['status'] | null;
  profile: string | null;
  progress: number;
  error: string | null;
}

/**
 * @param videoId - ID видео для подписки (null — хук неактивен)
 */
export function useSSE(videoId: string | null): UseSSEResult {
  const [status, setStatus] = useState<TranscodeProgress['status'] | null>(null);
  const [profile, setProfile] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!videoId) return;

    // Формируем URL SSE эндпоинта
    const baseUrl = import.meta.env.VITE_API_URL ?? '';
    const token = getAuthToken();
    const authQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    const sseUrl = `${baseUrl}/api/videos/${videoId}/status${authQuery}`;

    const source = new EventSource(sseUrl);

    source.onmessage = (event: MessageEvent<string>) => {
      let data: TranscodeProgress;
      try {
        data = JSON.parse(event.data) as TranscodeProgress;
      } catch {
        // Некорректный JSON — пропускаем
        return;
      }

      setStatus(data.status);
      setProfile(data.profile);
      setProgress(data.progress);

      if (data.error) {
        setError(data.error);
      }

      // Закрываем SSE-соединение при финальном статусе
      if (data.status === 'ready' || data.status === 'failed') {
        source.close();
      }
    };

    source.onerror = () => {
      // EventSource автоматически реконнектится — логируем но не паникуем
      // Если статус уже финальный — ошибка ожидаема (сервер закрыл поток)
      if (status === 'ready' || status === 'failed') {
        source.close();
      }
    };

    // Cleanup при размонтировании или смене videoId
    return () => {
      source.close();
    };
  }, [videoId]);  // status намеренно не в deps — используем snapshot в onerror

  return { status, profile, progress, error };
}
